import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../../utils/api';
import { Button, Card } from '../../shared/components/ui';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { buildAppUrl, buildGoogleAuthUrl, readApp, readProgramId } from '../../shared/utils/appUrl';
import { AlertCircle, ShieldCheck, MailQuestion } from 'lucide-react';
import { LegalLinks } from '../../shared/components/LegalLinks';

function LoginPage() {
  const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [orgName, setOrgName] = useState<string | null>(null);

 const dbId = readProgramId(searchParams);
 const app = readApp(searchParams) ?? 'admin';
 const mode = searchParams.get('mode');
 // Historical-campaign viewer (ADR-074 item 3, DL-261): carried through the OAuth round trip so
 // reauth lands back on the campaign that was clicked, not the live dashboard.
 const campaign = searchParams.get('campaign');
 const ssoToken = searchParams.get('sso_token');
 const isExpired = searchParams.get('expired') === 'true';
 const authError = searchParams.get('auth_error');

 // Surface session-expired and OAuth failure messages
 useEffect(() => {
  if (isExpired && !error) {
   // eslint-disable-next-line react-hooks/set-state-in-effect
   setError('Your session has expired. Please sign in again to continue.');
  } else if (authError && !error) {
   setError(authError);
  }
 }, [isExpired, authError, error]);

 // Pre-auth Program identification (mirrors VolunteerGateway.tsx) — so the login screen shows
 // which Program/environment it's for, not just a generic product title.
 useEffect(() => {
  if (!dbId) return;

  const loadOrg = async () => {
   try {
    const name = await apiCall<string>('getOrganizationName');
    setOrgName(name);
   } catch (err) {
    console.error('Failed to load org name:', err);
   }
  };
  loadOrg();
 }, [dbId]);

 useDocumentTitle(orgName, app === 'admin' ? 'Admin Portal' : 'Login');

 // ADR-115: `sso_token` in the URL is a short-lived, single-use login code, not the session token.
 // The real token only ever arrives in the redeem response body. The ref stops React 18 StrictMode's
 // dev-only double effect run from redeeming the (already consumed) code a second time.
 const redeemedCodeRef = useRef<string | null>(null);

 useEffect(() => {
  if (!ssoToken || redeemedCodeRef.current === ssoToken) return;
  redeemedCodeRef.current = ssoToken;

  const redeem = async () => {
   try {
    const { token } = await apiCall<{ token: string }>('redeemSsoLoginCode', ssoToken);
    // ADR-129: the Ops Hub keeps its own token (same key the emailed-code login uses); the Admin Portal's
    // session_token stays separate, so signing in to one never replaces the other.
    if (app === 'ops') {
     localStorage.setItem('volunteer_session_token', token);
    } else {
     sessionStorage.setItem('session_token', token);
    }
    // replace: the tokenized /auth/login URL must not stay in browser history.
    if (mode === 'provision') {
     // Return to the Welcome Page to complete the provisioning, keeping the mode and app context
     navigate(buildAppUrl('', { app: 'provision' }, { mode: 'provision' }), { replace: true });
    } else {
     navigate(buildAppUrl('', { app, programId: dbId }, { campaign }), { replace: true });
    }
   } catch (err) {
    setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
   }
  };
  redeem();
 }, [ssoToken, mode, app, dbId, campaign, navigate]);

 const handleGoogleLogin = () => {
  let cfApiUrl = import.meta.env.VITE_CF_API_URL;
  
  // Fallback for local development if .env is missing or misconfigured
  if (!cfApiUrl && window.location.hostname === 'localhost') {
   cfApiUrl = 'http://localhost:8787';
  }

  if (!cfApiUrl) {
   setError('Cloudflare API URL not configured. Please check your .env.local file.');
   return;
  }

  setLoading(true);
  
  // Build parameters for our backend auth endpoint
  window.location.href = buildGoogleAuthUrl(cfApiUrl, { app, programId: dbId }, { mode, campaign });
 };

 return (
  <div className="min-h-screen flex items-center justify-center bg-brand-surface px-4 py-12 sm:px-6 lg:px-8 font-sans">
   <div className="max-w-md w-full">
    <div className="text-center mb-8">
     <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-command rounded-xl shadow-xl shadow-precision mb-6 animate-in zoom-in duration-500">
      <ShieldCheck className="text-white w-8 h-8" />
     </div>
     <h2 className="text-header-xl font-bold text-brand-command tracking-tight uppercase">
      {orgName || 'Gift Request Manager'}
     </h2>
     {orgName && (
      <p className="mt-1 text-badge text-brand-label font-bold uppercase tracking-infrastructure">
       Gift Request Manager
      </p>
     )}
     <p className="mt-2 text-primary text-brand-label font-bold uppercase tracking-infrastructure">
      {app === 'admin' ? 'Admin Portal Access' : 'System Access'}
     </p>
    </div>

    <Card className="p-8 shadow-2xl border-white/50 bg-white/80 backdrop-blur-sm relative overflow-hidden">
     <div className="absolute -right-8 -top-8 w-24 h-24 bg-brand-surface rounded-full blur-2xl opacity-50" />
     
     {error && (
      <div className="mb-6 bg-danger-surface border-l-4 border-danger p-4 rounded flex items-start animate-in fade-in duration-200">
       <AlertCircle className="text-danger h-5 w-5 mr-3 flex-shrink-0 mt-0.5" />
       <p className="text-primary text-danger font-bold uppercase tracking-tight leading-tight">{error}</p>
      </div>
     )}

     <div className="space-y-8 relative z-10">
      <div className="space-y-4">
       <Button 
        variant="primary" 
        className="w-full h-12 text-primary font-bold uppercase tracking-infrastructure shadow-xl shadow-brand-command/10 flex items-center justify-center gap-3"
        onClick={handleGoogleLogin}
        disabled={loading}
       >
        <img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" className="w-5 h-5 bg-white rounded-full p-0.5" alt="G" />
        Sign in with Google
       </Button>
       <LegalLinks variant="agree" />
       
       {app === 'admin' ? (
        <p className="text-badge text-brand-label text-center font-bold uppercase tracking-infrastructure px-4">
         High-privilege access requires an authorized Google Account.
        </p>
       ) : null}
      </div>

      {app !== 'admin' && app !== 'ops' && (
       <div className="pt-6 border-t border-brand-edge animate-in slide-in-from-bottom-2 duration-500">
        <div className="flex items-start gap-4 bg-brand-surface p-4 rounded-xl">
         <MailQuestion className="w-6 h-6 text-brand-command flex-shrink-0 mt-0.5" />
         <div>
          <h4 className="text-badge font-bold text-brand-command uppercase tracking-infrastructure">Regular Volunteers</h4>
          <p className="text-badge font-semibold text-brand-command leading-relaxed">
           Please use the secure link sent to your Phone or Email. 
           If you have lost your link, please see your Site Coordinator for immediate access.
          </p>
         </div>
        </div>
       </div>
      )}
     </div>
    </Card>

    <div className="mt-12 text-center">
     <p className="text-badge text-brand-label/40 font-bold uppercase tracking-infrastructure">
      &copy; {new Date().getFullYear()} RockStop Solutions, LLC.
     </p>
    </div>
   </div>
  </div>
 );
}

export default LoginPage;
